import { ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  BaseInquiryShiftsWithFlex,
  BaseShiftIconsWithFlex,
  BaseShiftsWithFlexArray,
  ShiftPlanListBubbleItemInterface,
  SimilarInquiriesDateInterface,
  SimilarInquiryMappedInterface,
} from 'base';
import { ShiftPlanMdColumnInterface } from '../../../../../../_interfaces/base-customer-shift-plan-md.interface';
import { BaseCustomerSimilarInquiriesMdScrollService } from '../../../../../../_services/base-customer-similar-inquiries-md-scroll.service';

/**
 * One similar inquiry inside the mobile similar-inquiries panel: a header naming the inquiry and
 * its shift columns, then one row per calendar day. Rows are the organizer's height and follow the
 * same day order as the panel's calendar gutter, so the columns read across as a single grid — and
 * scroll as one, through the panel's shared offset.
 */
@Component({
  selector: 'base-customer-similar-inquiries-md-inquiry',
  templateUrl: './base-customer-similar-inquiries-md-inquiry.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaseCustomerSimilarInquiriesMdInquiryComponent implements OnInit, OnDestroy {
  @ViewChild('scrollBody') scrollBody: ElementRef<HTMLElement>;

  @Input() inquiry: SimilarInquiryMappedInterface;

  /** Same order as BaseSimilarInquiriesService.generateBubbles(), which iterates the same enum. */
  readonly columns: ShiftPlanMdColumnInterface[] = BaseShiftsWithFlexArray.map((shift) => ({
    shift,
    labelKey: shift,
    icon: shift === BaseInquiryShiftsWithFlex.flex ? '' : BaseShiftIconsWithFlex[shift],
  }));

  readonly shiftTypes = BaseInquiryShiftsWithFlex;

  private destroy$ = new Subject<void>();

  constructor(private scrollService: BaseCustomerSimilarInquiriesMdScrollService) {}

  ngOnInit(): void {
    this.scrollService.scrollTop$.pipe(takeUntil(this.destroy$)).subscribe((scrollTop) => {
      const element = this.scrollBody?.nativeElement;

      // Sub-pixel differences are the browser's own rounding of the offset this box already holds;
      // writing them back would bounce the shared value between the columns.
      if (element && Math.abs(element.scrollTop - scrollTop) >= 1) {
        element.scrollTop = scrollTop;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onBodyScroll(): void {
    this.scrollService.setScrollTop(this.scrollBody.nativeElement.scrollTop);
  }

  trackByColumn(_: number, item: ShiftPlanMdColumnInterface): string {
    return item.shift;
  }

  trackByDate(_: number, item: SimilarInquiriesDateInterface): string {
    return item.date;
  }

  trackByBubble(_: number, item: ShiftPlanListBubbleItemInterface): string {
    return item.shift;
  }
}
